import { intervalToDuration, isAfter } from 'date-fns';
import { useEffect, useState } from 'react';

export interface CountdownTime {
  minutes: number;
  seconds: number;
}

export function getExpiryCountdown(confirmableUntil: string): CountdownTime {
  const now = new Date();
  const end = new Date(confirmableUntil);

  if (isAfter(now, end)) {
    return { minutes: 0, seconds: 0 };
  }

  const duration = intervalToDuration({ start: now, end });

  return {
    minutes: duration.minutes ?? 0,
    seconds: duration.seconds ?? 0,
  };
}

/**
 * Per-second countdown to a `confirmableUntil` timestamp. Returns `null`
 * when no timestamp is given. Shared by the in-progress purchase booking
 * (booking-level `confirmableUntil`) and the change-booking flow (the
 * in-flight exchange operation's `confirmableUntil`).
 */
export function useExpiryCountdown(
  confirmableUntil?: string
): CountdownTime | null {
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);

  useEffect(() => {
    if (!confirmableUntil) {
      setCountdown(null);
      return;
    }

    setCountdown(getExpiryCountdown(confirmableUntil));

    const interval = setInterval(() => {
      setCountdown(getExpiryCountdown(confirmableUntil));
    }, 1000);

    return () => clearInterval(interval);
  }, [confirmableUntil]);

  return countdown;
}
